import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import VideoPlayer from '../player';
import firebase from 'firebase';
import { isBbcContent } from 'utils/isBbcContent';
import { createPlaybackTracker, postPlaybackData } from 'utils/playbackAnalytics';
import {
  createAvTracker,
  destroyAvTracker,
  trackAvEnd,
  trackAvPause,
  trackAvPlay
} from 'utils/bbcAvTracker';

/**
 * True while videojs-contrib-ads is playing an ad (IMA pre-roll). BBC events must
 * never fire for ad playback. player.ads is only defined once player.ima() has
 * run, i.e. for non-subscribers, so the guard has to tolerate its absence.
 */
function isInAdMode(player) {
  return Boolean(
    player &&
      player.ads &&
      typeof player.ads.isInAdMode === 'function' &&
      player.ads.isInAdMode()
  );
}

const VideoJs = ({
  videoUrl,
  currentTrack,
  currentUser,
  userProfiles,
  setEpisodeIndex,
  setSeriesIndex,
  series,
  episodeIndex,
  seriesLength,
  seriesIndex,
  subtitle,
  id: seriesId,
  setVideoDuration,
  trailerModal,
  setTrailerEnd,
  from,
  playerInstanceReady,
  isGpSubscribed,
  posterUrl,
  hasTvodSub,
  newSeriesSlug
}) => {
  const playerRef = React.useRef(null);
  const history = useHistory();
  const [url, setUrl] = React.useState(videoUrl);
  const [videoEnd, setVideoEnd] = React.useState(false);
  const [showSkipTrailerBtn, setShowSkipTrailerBtn] = React.useState(false);
  const title = window?.platform_info?.title;
  const gpUser = JSON.parse(localStorage.getItem(`gp_user_${title}`));
  const robiUser = JSON.parse(localStorage.getItem(`robi_user_${title}`));
  const blUser = JSON.parse(localStorage.getItem(`bl_user_${title}`));
  const [playerReady, setPlayerReady] = React.useState(false);
  const isPlayingRef = React.useRef(false);
  const bbcTrackerIdRef = React.useRef(null);
  // Series info (banner) and the season list, for the playback analytics payload.
  const { banner, tracks } = useSelector((state) => state.video);
  const playbackTrackerRef = React.useRef(createPlaybackTracker());
  const seriesPath = newSeriesSlug
    ? `/content/play/series/${newSeriesSlug}`
    : `/series/${seriesId}`;

  const subTitles =
    subtitle?.length > 0 &&
    subtitle.map((sub) => {
      console.log(`https://subtitles.rockstreamer.com/${sub.path}`);
      return {
        src: `https://subtitles.rockstreamer.com/${sub.path}`,
        kind: 'subtitles',
        srclang: sub.languageCode,
        label: sub.languageCode
      };
    });
  React.useEffect(() => {
    setUrl(videoUrl);
  }, [videoUrl]);

  /* Handle beforeunload to log data if the video was playing
   *  Until closing browser tab
   */
  React.useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isPlayingRef.current) {
        analytics.logEvent('content_tracking_play', {
          content_id: currentTrack?.id.toString(),
          platform_name: window.platform_info.api_key,
          content_duration: playerRef.current.currentTime()
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  /* to log data if the video was playing
   *  Until swap minimize which triggers inactive tab
   */
  React.useEffect(() => {
    if (isPlayingRef.current) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // tab is changed
          analytics.logEvent('content_tracking_play', {
            content_id: currentTrack?.id.toString(),
            platform_name: window.platform_info.api_key,
            content_duration: playerRef.current.currentTime()
          });
        } else {
          // tab is active
        }
      });
    }
  }, []);

  /*
   *  Following effect calls when video ends
   *
   *  "from" parameter indicates only Slug based(v2) details page
   *  -> Having legacy backward compatibility for old id based(v1) details page,
   *     in addition making it work with redesigned details and player (v3) version
   *     which is again inspired from old query param based (v1) series player
   *     Changed lines in v3 can be found by searching
   *     "both v3 and v1 compatible"
   */
  React.useEffect(() => {
    if (videoEnd === true) {
      if (currentTrack?.type === 'catchupTV' || currentTrack?.type === 'catchupTVEpisode') {
        setEpisodeIndex((prev) => prev + 1);
        history.push({
          pathname: `/catchupTV/${seriesId}`,
          search: '?' + new URLSearchParams({ episode_index: episodeIndex + 2 }).toString()
        });
      } else {
        setEpisodeIndex((prev) => prev + 1);
        // both v3 and v1 compatible
        if (!from) {
          history.push({
            pathname: seriesPath,
            search:
              '?' +
              new URLSearchParams({ season_index: 1 }).toString() +
              '&' +
              new URLSearchParams({ episode_index: episodeIndex + 2 }).toString()
          });
        }
        if (episodeIndex == series.contents.length - 1) {
          setSeriesIndex((prev) => prev + 1);
          setEpisodeIndex(0);
          // both v3 and v1 compatible
          if (!from) {
            history.push({
              pathname: seriesPath,
              search:
                '?' +
                new URLSearchParams({ season_index: seriesIndex + 2 }).toString() +
                '&' +
                new URLSearchParams({ episode_index: 1 }).toString()
            });
          }

          if (seriesIndex == seriesLength - 1) {
            // both v3 and v1 compatible
            if (!from) {
              history.push({
                pathname: seriesPath,
                search:
                  '?' +
                  new URLSearchParams({ season_index: 1 }).toString() +
                  '&' +
                  new URLSearchParams({ episode_index: 1 }).toString()
              });
            }
            setSeriesIndex(0);
            setEpisodeIndex(0);
          }
        }
      }
      setVideoEnd(false);
    }
  }, [videoEnd]);

  /*
   *  Following function calls when Skip Episode button in player get clicked
   *
   *  "from" parameter indicates only Slug based(v2) details page
   *  -> Having Legacy backward compatibility for old id based(v1) details page,
   *     in addition making it work with redesigned details and player (v3) version
   *     which is again inspired from old query param based (v1) series player
   *     Changed lines in v3 can be found by searching
   *     "both v3 and v1 compatible"
   */
  function onSkipEpisode() {
    const searchParams = new URLSearchParams(location.search);
    const episodeParamIndex = searchParams.get('episode_index');
    const seasonParamIndex = searchParams.get('season_index');
    setEpisodeIndex((prev) => prev + 1);
    // both v3 and v1 compatible
    if (!from) {
      history.push({
        pathname: seriesPath,
        search:
          '?' +
          new URLSearchParams({ season_index: 1 }).toString() +
          '&' +
          new URLSearchParams({ episode_index: Number(episodeParamIndex) + 1 }).toString()
      });
    }
    if (episodeIndex == series.contents.length - 1) {
      setSeriesIndex((prev) => prev + 1);
      setEpisodeIndex(0);
      // both v3 and v1 compatible
      if (!from) {
        history.push({
          pathname: seriesPath,
          search:
            '?' +
            new URLSearchParams({ season_index: Number(seasonParamIndex) + 1 }).toString() +
            '&' +
            new URLSearchParams({ episode_index: 1 }).toString()
        });
      }

      if (seriesIndex == seriesLength - 1) {
        // both v3 and v1 compatible
        if (!from) {
          history.push({
            pathname: seriesPath,
            search:
              '?' +
              new URLSearchParams({ season_index: 1 }).toString() +
              '&' +
              new URLSearchParams({ episode_index: 1 }).toString()
          });
        }
        setSeriesIndex(0);
        setEpisodeIndex(0);
      }
    }
  }

  React.useEffect(() => {
    if ((gpUser || blUser) && isGpSubscribed === 'OK') {
      setShowSkipTrailerBtn(false);
    } else if (
      currentTrack?.premium &&
      !userProfiles?.subscribe &&
      currentTrack?.trailer &&
      !currentTrack?.tvod
    ) {
      setShowSkipTrailerBtn(true);
    }
  }, [isGpSubscribed]);

  React.useEffect(() => {
    return () => {
      if (showSkipTrailerBtn == true) {
        setShowSkipTrailerBtn(false);
      }
    };
  }, []);

  const onHideSkipTrailer = () => {
    setTrailerEnd(true);
    setShowSkipTrailerBtn(false);
  };

  React.useEffect(() => {
    const handlePopState = () => {
      // window.location.replace(
      //   `https://youtube.com`
      // );
      if (
        playerReady &&
        (gpUser?.partner == 'GP_MYGP' ||
          robiUser?.partner == 'ROBI_MYROBI' ||
          blUser?.partner == 'BANGLALINK_MYBL') &&
        playerRef.current.isFullscreen()
      ) {
        playerRef.current.exitFullscreen();
      }
    };
    if (
      (gpUser?.partner == 'GP_MYGP' ||
        robiUser?.partner == 'ROBI_MYROBI' ||
        blUser?.partner == 'BANGLALINK_MYBL') &&
      window.history.length == 2
    ) {
      window.history.pushState(null, null, document.URL);
    }
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [playerReady]);

  const autoplayStatus =
    currentTrack?.live ||
    currentTrack?.type == 'tv' ||
    (gpUser?.partner == 'GP_MYGP' && currentTrack?.tvodexclusive && hasTvodSub)
      ? true
      : gpUser?.partner == 'GP_MYGP' ||
        robiUser?.partner == 'ROBI_MYROBI' ||
        blUser?.partner == 'BANGLALINK_MYBL'
      ? false
      : !trailerModal;

  // const overrideNative = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
  const overrideNative = false;

  const videoJsOptions = {
    // lookup the options in the docs for more options
    nativeVideoTracks: !overrideNative,
    nativeAudioTracks: !overrideNative,
    nativeTextTracks: !overrideNative,
    autoplay: autoplayStatus,
    // autoplay: false,
    playsinline: true,
    crossorigin: 'anonymous',
    controls: true,
    responsive: true,
    poster: `${process.env.REACT_APP_IMAGE_URL}400xauto/${posterUrl}`,
    sources: [
      {
        src: url,
        type: 'application/x-mpegURL',
        label: 'SD'
      },
      {
        src: url,
        type: 'application/x-mpegURL',
        label: 'HD'
      }
    ],
    userActions: {
      hotkeys: {}
    },
    tracks: subTitles,
    preload: 'auto',
    liveui: true,
    html5: {
      vhs: {
        enableLowLatency: true, // If applicable
        maxBufferLength: 60,
        startLevel: 0, // Automatically choose the best available quality
        limitRenditionByPlayerDimensions: false,
        useDevicePixelRatio: false,
        bandwidth: 500000, // (~0.5 Mbps) forces a conservative start
        overrideNative: true,
        debug: true
      }
    },
    plugins: {
      // mux: {
      //   debug: false,
      //   data: {
      //     env_key: 'iljf25jpf0nellio2h5hfo9lt', // required
      //     // Site Metadata
      //     video_id: currentTrack?.id,
      //     video_title: currentTrack?.title
      //   }
      // },
      httpSourceSelector: {
        default: 'low'
      }
    }

    // tracks: [
    //   {
    //     src: 'https://kot-politiken.s3-eu-west-1.amazonaws.com/2019/114_en.vtt.txt',
    //     kind: 'subtitles',
    //     srclang: 'en',
    //     label: 'English'
    //   },
    //   { src: 'https://domain/mysub.vtt', kind: 'subtitles', srclang: 'ban', label: 'Bangla' }
    // ]
  };

  /*
   *  BBC World Service partner A/V analytics — /bbc-urdu syndicated clips only.
   *  https://bbc.github.io/ws-partner-analytics/JavaScriptAV
   *
   *  Guarded three ways, because BBC's spec requires that no other publisher's
   *  content ever reports to them:
   *    1. the content id must be on the BBC allowlist (utils/isBbcContent)
   *    2. the source must be the content itself, not a trailer — PlayNowCard
   *       hands us currentTrack.trailerPath for premium/unsubscribed viewers
   *    3. no events while an IMA ad is on screen (see isInAdMode)
   */
  const isContentPlayback = Boolean(currentTrack?.path) && url === currentTrack?.path;

  /**
   * Resolve BBC provenance and register the clip, once per content id.
   * Async because the allowlist may still be in flight when playback starts.
   * @returns {Promise<string|null>} trackerId, or null when this is not BBC content.
   */
  const ensureBbcTracker = React.useCallback(async () => {
    if (bbcTrackerIdRef.current) return bbcTrackerIdRef.current;
    if (!currentTrack?.id || !isContentPlayback) return null;

    const isBbc = await isBbcContent(currentTrack.id);
    if (!isBbc) return null;

    const trackerId = `bbc-av-${currentTrack.id}`;
    const created = createAvTracker(trackerId, {
      // Interim: our own content id. Swap to the BBC-issued asset id once BBC
      // confirms which identifier they reconcile against.
      clipId: currentTrack.id,
      clipName: currentTrack.title,
      clipDuration: currentTrack.durationInSeconds,
      mediaType: 'video',
      section: currentTrack.genres?.map((genre) => genre.name).join(' | '),
      player: 'videojs'
    });
    if (!created) return null;

    bbcTrackerIdRef.current = trackerId;
    return trackerId;
  }, [
    currentTrack?.id,
    currentTrack?.title,
    currentTrack?.durationInSeconds,
    currentTrack?.genres,
    isContentPlayback
  ]);

  // handlePlayerReady runs once, when the player is first created, so its
  // listeners would otherwise close over the first clip forever — the player is
  // reused (player.src()) when navigating clip to clip. Reach the current
  // resolver through a ref instead.
  const ensureBbcTrackerRef = React.useRef(ensureBbcTracker);
  React.useEffect(() => {
    ensureBbcTrackerRef.current = ensureBbcTracker;
  }, [ensureBbcTracker]);

  /*
   * Mirrors what BBC currently believes about this session: true once avPlay has
   * been sent, false once avPause or avEnd has. Every report goes through the two
   * helpers below, so a play can only ever follow a pause and vice versa.
   *
   * This exists because playback and tracker readiness race, and in production the
   * player wins. The clip's 'play' events fire while the content API call is still
   * in flight, so currentTrack is null, ensureBbcTracker bails at its first guard,
   * and no avPlay is sent. The tracker is then created moments later by the effect
   * below — but the video is already playing, so no further 'play' event ever
   * arrives to report. BBC saw a clip registration and then silence.
   */
  const bbcPlayingRef = React.useRef(false);

  const reportBbcPlay = React.useCallback((offset) => {
    const trackerId = bbcTrackerIdRef.current;
    if (!trackerId || bbcPlayingRef.current) return;
    bbcPlayingRef.current = true;
    trackAvPlay(trackerId, offset);
  }, []);

  const reportBbcPause = React.useCallback((offset) => {
    const trackerId = bbcTrackerIdRef.current;
    if (!trackerId || !bbcPlayingRef.current) return;
    bbcPlayingRef.current = false;
    trackAvPause(trackerId, offset);
  }, []);

  const reportBbcPlayRef = React.useRef(reportBbcPlay);
  React.useEffect(() => {
    reportBbcPlayRef.current = reportBbcPlay;
  }, [reportBbcPlay]);

  // Resolve the clip as soon as it is mounted, so the tracker is ready early. The
  // play itself is reported from the player's own events below — this effect can
  // run before playerRef is even assigned, so it is not a reliable place to do it.
  React.useEffect(() => {
    if (currentTrack?.id && isContentPlayback) ensureBbcTracker();
  }, [ensureBbcTracker, currentTrack?.id, isContentPlayback]);

  // A different clip means a different tracker, and a fresh play state.
  React.useEffect(() => {
    return () => {
      bbcPlayingRef.current = false;
      if (bbcTrackerIdRef.current) {
        destroyAvTracker(bbcTrackerIdRef.current);
        bbcTrackerIdRef.current = null;
      }
    };
  }, [currentTrack?.id]);

  /**
   * Payload for one watched segment, or null when this playback must not be
   * reported at all: anything that is not a video or a series episode (catchup,
   * audio, live channels), a trailer standing in for premium content, or an
   * episode whose season cannot be resolved.
   */
  const buildPlaybackPayload = React.useCallback(
    (segment) => {
      const contentType = currentTrack?.type;
      if (contentType !== 'video' && contentType !== 'episode') return null;
      if (!currentTrack?.id || !isContentPlayback) return null;

      let seriesId = null;
      let seasonId = null;

      if (contentType === 'episode') {
        // The season that actually holds this episode. tracks[seriesIndex] would
        // follow the season tab the viewer is browsing, which is not necessarily
        // the one playing.
        const playingSeason = tracks?.find((season) =>
          season?.contents?.some((episode) => episode?.id === currentTrack.id)
        );
        const resolvedSeasonId = playingSeason?.id || series?.id;
        if (!banner?.id || !resolvedSeasonId) return null;

        seriesId = String(banner.id);
        seasonId = String(resolvedSeasonId);
      }

      return {
        contentId: String(currentTrack.id),
        contentName: currentTrack.title,
        seriesId,
        seasonId,
        platformName: 'web',
        contentType,
        isLive: Boolean(currentTrack.live),
        playbackPosition: segment.playbackPosition,
        duration: segment.duration
      };
    },
    [currentTrack, banner, tracks, series, isContentPlayback]
  );

  const flushPlayback = React.useCallback(
    (position, options) => {
      const segment = playbackTrackerRef.current.take(position);
      if (!segment) return;

      const payload = buildPlaybackPayload(segment);
      if (!payload) return;

      postPlaybackData(payload, options);
    },
    [buildPlaybackPayload]
  );

  // Same reason as ensureBbcTrackerRef above: the player's listeners are
  // registered once, so they must not close over the first episode forever.
  const flushPlaybackRef = React.useRef(flushPlayback);
  React.useEffect(() => {
    flushPlaybackRef.current = flushPlayback;
  }, [flushPlayback]);

  // Reading the playhead can throw once the player has been torn down.
  const readPlayhead = React.useCallback(() => {
    try {
      return playerRef.current?.currentTime();
    } catch (error) {
      return undefined;
    }
  }, []);

  /*
   * Close the open segment when the viewer leaves: another episode (cleanup on
   * the content id), away from the player (unmount), or away from the page
   * entirely — the last one needs a keepalive request, since the browser
   * cancels in-flight XHRs on unload.
   */
  React.useEffect(() => {
    const handlePageHide = () => {
      flushPlaybackRef.current(readPlayhead(), { keepalive: true });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      flushPlaybackRef.current(readPlayhead());
    };
  }, [currentTrack?.id, readPlayhead]);

  const analytics = firebase.analytics();
  const handlePlayerReady = (player) => {
    playerRef.current = player;
    playerInstanceReady(player);
    console.log('Analytics logging event');
    analytics.logEvent('content_tracking', {
      content_id: currentTrack?.id.toString(),
      platform_name: window.platform_info.api_key,
      content_name: currentTrack?.title,
      type: currentTrack?.type,
      cast: currentTrack?.casts?.map((cast) => cast.name).join(','),
      createdAt: currentTrack?.createdAt,
      user_id: currentUser?.id
    });

    player.on('dispose', () => {
      analytics.logEvent('content_tracking_play', {
        content_id: currentTrack?.id.toString(),
        platform_name: window.platform_info.api_key,
        content_duration: playerRef.current.currentTime()
      });

      // Leaving mid-playback is a pause per spec, not an end.
      reportBbcPause(Math.floor(playerRef.current.currentTime() || 0));
      isPlayingRef.current = false;
    });

    player.on('ready', () => {
      console.log('Player is ready');
      setPlayerReady(true);
    });

    player.on('play', () => {
      const gpUser = JSON.parse(localStorage.getItem(`gp_user_${title}`));
      const robiUser = JSON.parse(localStorage.getItem(`robi_user_${title}`));
      const blUser = JSON.parse(localStorage.getItem(`bl_user_${title}`));
      const userAgent = navigator.userAgent;
      if (
        gpUser?.partner == 'GP_MYGP' ||
        robiUser?.partner == 'ROBI_MYROBI' ||
        blUser?.partner == 'BANGLALINK_MYBL'
      ) {
        console.log('Content Playing event');
        player.requestFullscreen();
        player.isFullscreen(true);
      }
      isPlayingRef.current = true;
      console.log('IsplayingRef in play', isPlayingRef);

      if (isInAdMode(player)) return;
      // Capture the playhead now; the tracker lookup may still be resolving. If it
      // resolves after playback has already begun, the effect above reports it.
      const offset = Math.floor(player.currentTime() || 0);
      ensureBbcTrackerRef.current().then(() => reportBbcPlayRef.current(offset));
    });

    player.on('pause', () => {
      isPlayingRef.current = false;
      console.log('IsplayingRef in pause', isPlayingRef);

      // videojs fires 'pause' just before 'ended'; that transition is an avEnd.
      if (isInAdMode(player) || player.ended()) return;
      reportBbcPause(Math.floor(player.currentTime() || 0));
    });

    /*
     * The reliable signal that BBC content is actually on screen.
     *
     * The 'play' event alone is not enough: it can fire before the clip has been
     * resolved (so there is no tracker yet), and videojs-contrib-ads is already in
     * ad mode when the pre-roll is pending, which suppresses it. Either way the
     * event never comes back, because the video is by then already playing.
     *
     * Real playhead progress outside ad mode is unambiguous, and reportBbcPlay is
     * idempotent, so whichever earlier signal was missed this repairs it within a
     * quarter of a second. It also naturally reports the resume after a pre-roll.
     */
    player.on('timeupdate', () => {
      if (bbcPlayingRef.current) return;
      if (isInAdMode(player) || player.paused()) return;
      if (!(player.currentTime() > 0)) return;

      const offset = Math.floor(player.currentTime());
      ensureBbcTrackerRef.current().then(() => reportBbcPlayRef.current(offset));
    });

    /*
     * Playback analytics. A listener of its own rather than a line inside the
     * BBC timeupdate handler above: that one returns early as soon as its play
     * has been reported, which would stop the sampling after a quarter second.
     */
    player.on('timeupdate', () => {
      if (isInAdMode(player) || player.paused()) return;
      playbackTrackerRef.current.sample(player.currentTime());
    });

    player.on('seeking', () => {
      // No position passed on purpose: currentTime() is already the seek target,
      // so the tracker's own last sample is where the viewer actually watched to.
      flushPlaybackRef.current();
    });

    player.on('dispose', () => {
      let position;
      try {
        position = player.currentTime();
      } catch (error) {
        position = undefined;
      }
      flushPlaybackRef.current(position);
    });

    player.on('fullscreenchange', () => {
      console.log('Checking full screen event');
    });

    // you can handle player events here
    player.on('waiting', () => {
      console.log('player is waiting');
    });

    player.on('adend', function () {
      console.log('Content resumed');
      player.play();
      player.muted(false);
      // player.volume(100);
    });
  };

  const onVideoEnd = () => {
    if (
      (currentTrack?.premium && !userProfiles?.subscribe) ||
      (currentTrack?.tvod && !userProfiles?.subscribe) ||
      (currentTrack?.tvodexclusive && !hasTvodSub)
    ) {
      setTrailerEnd(true);
    }
    analytics.logEvent('content_tracking_play', {
      content_id: currentTrack?.id.toString(),
      platform_name: window.platform_info.api_key,
      content_duration: playerRef.current.duration()
    });

    if (bbcTrackerIdRef.current) {
      trackAvEnd(bbcTrackerIdRef.current);
      isPlayingRef.current = false;
      bbcPlayingRef.current = false;
      // Drop it so a replay registers a fresh tracker (and a new BBC session).
      destroyAvTracker(bbcTrackerIdRef.current);
      bbcTrackerIdRef.current = null;
    }

    setEpisodeIndex && setVideoEnd(true);
  };

  return (
    <VideoPlayer
      options={videoJsOptions}
      onEnd={onVideoEnd}
      currentTrack={currentTrack}
      userProfiles={userProfiles}
      onReady={handlePlayerReady}
      setVideoDuration={setVideoDuration}
      onSkipEpisode={onSkipEpisode}
      seriesId={seriesId}
      showSkipTrailerBtn={showSkipTrailerBtn}
      onHideSkipTrailer={onHideSkipTrailer}
      seriesIndex={seriesIndex}
      episodeIndex={episodeIndex}
      from={from}
      setEpisodeIndex={setEpisodeIndex}
      setSeriesIndex={setSeriesIndex}
      newSeriesSlug={newSeriesSlug}
      autoplayStatus={autoplayStatus}
    />
  );
};

export default VideoJs;
